import React, { useState, useMemo } from 'react';
import {
  ListTodo,
  Plus,
  MoreHorizontal,
  Trash2,
  Edit2,
  CheckCircle2,
  Circle,
  Clock,
  AlertCircle,
  FileCode,
  X,
  Wrench,
  Play,
  Bot,
} from 'lucide-react';
import { useTasks, useCreateTask, useUpdateTask, useDeleteTask } from '../helpers/useTasksApi';
import { usePersonas } from '../helpers/usePersonasApi';
import { useEnqueueAiTask, useAiQueue } from '../helpers/useAiQueueApi';
import { Button } from './Button';
import { Input } from './Input';
import { Textarea } from './Textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './Select';
import { Badge } from './Badge';
import { Avatar, AvatarFallback, AvatarImage } from './Avatar';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
  DialogTrigger,
} from './Dialog';
import { Tabs, TabsList, TabsTrigger } from './Tabs';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from './DropdownMenu';
import { Skeleton } from './Skeleton';
import { QueuePriority } from '../helpers/schema';
import styles from './TaskQueuePanel.module.css';

interface TaskQueuePanelProps {
  sessionId: string;
  files?: Array<{ id: string; filePath: string }>;
  className?: string;
  onExecuteTask?: (taskId: string, taskTitle: string, taskDescription?: string) => void; // Callback to execute task with AI
}

type TaskStatus = 'pending' | 'in_progress' | 'completed' | 'blocked' | 'waiting_for_input';

export const TaskQueuePanel: React.FC<TaskQueuePanelProps> = ({
  sessionId,
  files = [],
  className,
  onExecuteTask,
}) => {
  const { data, isLoading } = useTasks(sessionId);
  const { data: personasData } = usePersonas();
  const { data: queueData } = useAiQueue(sessionId);
  const createMutation = useCreateTask();
  const updateMutation = useUpdateTask();
  const deleteMutation = useDeleteTask();
  const enqueueMutation = useEnqueueAiTask();

  const [filter, setFilter] = useState<'all' | TaskStatus>('all');
  const [isCreating, setIsCreating] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);

  // Form state
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [priority, setPriority] = useState<QueuePriority>('medium');
  const [fileId, setFileId] = useState<string>('_empty');
  const [assignedPersonaId, setAssignedPersonaId] = useState<string>('_none');

  // Available personas for assignment
  const availablePersonas = personasData?.personas ?? [];

  // Check if task is queued
  const isTaskQueued = (taskId: string) => {
    return queueData?.items.some(
      (item) => item.taskId === taskId && ['pending', 'processing'].includes(item.status),
    );
  };

  const getQueueStatus = (taskId: string) => {
    return queueData?.items.find(
      (item) => item.taskId === taskId && ['pending', 'processing'].includes(item.status),
    )?.status;
  };

  const resetForm = () => {
    setTitle('');
    setDescription('');
    setPriority('medium');
    setFileId('_empty');
    setAssignedPersonaId('_none');
    setIsCreating(false);
    setEditingId(null);
  };

  const handleCreate = async () => {
    if (!title.trim()) return;

    await createMutation.mutateAsync({
      sessionId,
      title,
      description,
      priority,
      fileId: fileId === '_empty' ? undefined : fileId,
      assignedPersonaId: assignedPersonaId === '_none' ? undefined : assignedPersonaId,
    });
    resetForm();
  };

  const handleUpdate = async () => {
    if (!editingId || !title.trim()) return;

    await updateMutation.mutateAsync({
      taskId: editingId,
      title,
      description,
      priority,
      fileId: fileId === '_empty' ? null : fileId,
      assignedPersonaId: assignedPersonaId === '_none' ? null : assignedPersonaId,
    });
    resetForm();
  };

  const startEdit = (task: any) => {
    setEditingId(task.id);
    setTitle(task.title);
    setDescription(task.description || '');
    setPriority(task.priority);
    setFileId(task.fileId || '_empty');
    setAssignedPersonaId(task.assignedPersona?.id || '_none');
    setIsCreating(true);
  };

  const handleQueueForAi = async (taskId: string) => {
    await enqueueMutation.mutateAsync({ taskId });
  };

  const handleDelete = async (taskId: string) => {
    await deleteMutation.mutateAsync({ taskId });
  };

  const handleStatusChange = async (taskId: string, status: string) => {
    await updateMutation.mutateAsync({ taskId, status });
  };

  const filteredTasks = useMemo(() => {
    if (!data?.tasks) return [];
    let tasks = [...data.tasks];

    if (filter !== 'all') {
      tasks = tasks.filter((t) => t.status === filter);
    }

    // Sort by priority (urgent > high > medium > low) then position/date
    const priorityWeight = { urgent: 4, high: 3, medium: 2, low: 1 };
    return tasks.sort((a, b) => {
      const weightDiff = priorityWeight[b.priority] - priorityWeight[a.priority];
      if (weightDiff !== 0) return weightDiff;
      return (a.position || 0) - (b.position || 0);
    });
  }, [data?.tasks, filter]);

  const getPriorityColor = (p: QueuePriority) => {
    switch (p) {
      case 'urgent':
        return 'destructive';
      case 'high':
        return 'warning';
      case 'medium':
        return 'secondary';
      case 'low':
        return 'outline';
      default:
        return 'outline';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'completed':
        return <CheckCircle2 size={14} className={styles.statusIconSuccess} />;
      case 'in_progress':
        return <Clock size={14} className={styles.statusIconProgress} />;
      case 'blocked':
        return <Wrench size={14} className={styles.statusIconBlocked} />; // "fix" icon
      case 'waiting_for_input':
        return <AlertCircle size={14} className={styles.statusIconBlocked} />; // needs attention
      default:
        return <Play size={14} className={styles.statusIconPending} />; // "start" icon
    }
  };

  return (
    <div className={`${styles.panel} ${className || ''}`}>
      <div className={styles.header}>
        <div className={styles.title}>
          <ListTodo size={16} className={styles.icon} />
          <span>Task Queue</span>
        </div>
        <Button
          size="sm"
          variant="outline"
          onClick={() => {
            resetForm();
            setIsCreating(!isCreating);
          }}
          disabled={isCreating && !editingId}
        >
          <Plus size={14} /> New Task
        </Button>
      </div>

      <div className={styles.tabsContainer}>
        <Tabs value={filter} onValueChange={(v) => setFilter(v as any)}>
          <TabsList className={styles.tabsList}>
            <TabsTrigger value="all">All</TabsTrigger>
            <TabsTrigger value="pending">Pending</TabsTrigger>
            <TabsTrigger value="waiting_for_input">Waiting</TabsTrigger>
            <TabsTrigger value="in_progress">In Progress</TabsTrigger>
            <TabsTrigger value="completed">Done</TabsTrigger>
          </TabsList>
        </Tabs>
      </div>

      {isCreating && (
        <div className={styles.createForm}>
          <div className={styles.formHeader}>
            <span className={styles.formTitle}>{editingId ? 'Edit Task' : 'New Task'}</span>
            <Button variant="ghost" size="icon-sm" onClick={resetForm}>
              <X size={14} />
            </Button>
          </div>
          <Input
            placeholder="Task Title"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            autoFocus
          />
          <Textarea
            placeholder="Description"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            rows={2}
          />
          <div className={styles.formRow}>
            <Select value={priority} onValueChange={(v) => setPriority(v as QueuePriority)}>
              <SelectTrigger className={styles.selectTrigger}>
                <SelectValue placeholder="Priority" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="urgent">Urgent</SelectItem>
                <SelectItem value="high">High</SelectItem>
                <SelectItem value="medium">Medium</SelectItem>
                <SelectItem value="low">Low</SelectItem>
              </SelectContent>
            </Select>

            <Select value={fileId} onValueChange={setFileId}>
              <SelectTrigger className={styles.selectTrigger}>
                <SelectValue placeholder="Link File" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="_empty">No File</SelectItem>
                {files.map((f) => (
                  <SelectItem key={f.id} value={f.id}>
                    {f.filePath.split('/').pop()}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className={styles.formRow}>
            <Select value={assignedPersonaId} onValueChange={setAssignedPersonaId}>
              <SelectTrigger className={styles.selectTrigger}>
                <SelectValue placeholder="Assign AI Persona" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="_none">No AI Assigned</SelectItem>
                {availablePersonas.map((p) => (
                  <SelectItem key={p.id} value={p.id}>
                    {p.icon} {p.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className={styles.formActions}>
            <Button variant="ghost" size="sm" onClick={resetForm}>
              Cancel
            </Button>
            <Button size="sm" onClick={editingId ? handleUpdate : handleCreate}>
              {editingId ? 'Update' : 'Create'}
            </Button>
          </div>
        </div>
      )}

      <div className={styles.list}>
        {isLoading ? (
          Array.from({ length: 3 }).map((_, i) => (
            <Skeleton key={i} className={styles.skeletonItem} />
          ))
        ) : filteredTasks.length === 0 ? (
          <div className={styles.emptyState}>
            <ListTodo size={32} />
            <p>No tasks found</p>
          </div>
        ) : (
          filteredTasks.map((task) => (
            <div key={task.id} className={styles.card}>
              <div className={styles.cardHeader}>
                <div className={styles.cardTitleRow}>
                  {getStatusIcon(task.status)}
                  <span
                    className={`${styles.cardTitle} ${task.status === 'completed' ? styles.completedTitle : ''}`}
                  >
                    {task.title}
                  </span>
                </div>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="ghost" size="icon-sm" className={styles.menuBtn}>
                      <MoreHorizontal size={14} />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuItem onClick={() => handleStatusChange(task.id, 'pending')}>
                      Mark Pending
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => handleStatusChange(task.id, 'in_progress')}>
                      Mark In Progress
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => handleStatusChange(task.id, 'completed')}>
                      Mark Completed
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => handleStatusChange(task.id, 'blocked')}>
                      Mark Blocked
                    </DropdownMenuItem>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem onClick={() => startEdit(task)}>
                      <Edit2 size={14} className="mr-2" /> Edit
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      onClick={() => handleDelete(task.id)}
                      className={styles.deleteItem}
                    >
                      <Trash2 size={14} className="mr-2" /> Delete
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>

              <div className={styles.cardMeta}>
                <Badge variant={getPriorityColor(task.priority)} className={styles.priorityBadge}>
                  {task.priority}
                </Badge>

                {task.fileId && (
                  <div className={styles.fileChip}>
                    <FileCode size={10} />
                    <span>
                      {files
                        .find((f) => f.id === task.fileId)
                        ?.filePath.split('/')
                        .pop() || 'File'}
                    </span>
                  </div>
                )}

                {task.assignedTo && (
                  <Avatar className={styles.avatar}>
                    <AvatarImage src={task.assignedTo.avatarUrl || undefined} />
                    <AvatarFallback>{task.assignedTo.displayName[0]}</AvatarFallback>
                  </Avatar>
                )}

                {task.assignedPersona && (
                  <div
                    className={styles.personaChip}
                    title={`Assigned to ${task.assignedPersona.name}`}
                  >
                    <span>{task.assignedPersona.icon}</span>
                    <span>{task.assignedPersona.name}</span>
                  </div>
                )}

                {task.assignedPersona && !isTaskQueued(task.id) && task.status !== 'completed' && (
                  <Button
                    variant="outline"
                    size="sm"
                    className={styles.queueBtn}
                    onClick={() => handleQueueForAi(task.id)}
                    disabled={enqueueMutation.isPending}
                    title="Queue task for AI execution"
                  >
                    <Bot size={14} />
                    Queue AI
                  </Button>
                )}

                {isTaskQueued(task.id) && (
                  <Badge variant="secondary" className={styles.queuedBadge}>
                    <Clock size={10} />
                    {getQueueStatus(task.id) === 'processing' ? 'Processing...' : 'Queued'}
                  </Badge>
                )}

                {onExecuteTask && !task.assignedPersona && (
                  <Button
                    variant="ghost"
                    size="sm"
                    className={styles.executeBtn}
                    onClick={() =>
                      onExecuteTask(task.id, task.title, task.description || undefined)
                    }
                    title="Execute with AI code assistant"
                  >
                    <Bot size={14} />
                    Execute
                  </Button>
                )}
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
};
